import uuidv4 from 'uuid/v4';
import {
    RECENT_NOTIFICATION_UPDATE,
    RECENT_CHAT_DATA,
    RECENT_RECALL_UPDATE,
    ACTIVE_CHAT_DATA,
    ACTIVE_CHAT_UPDATE,
    PROFILE_IMAGE_CHAT_DATA,
    RECENT_REMOVE_MESSAGE_UPDATE,
    RECENT_ROSTER_UPDATE,
    DELETE_SINGLE_CHAT,
    RESET_TO_DEFAULT,
    UPDATE_ROSTER_LAST_MESSAGE,
    UPDATE_GROUP_NAME,
    RESET_RECENT_CHAT,
    UPDATE_MSG_BY_LAST_MSGID,
    MODAL_ACTIVE_CLASS,
    RECONNECT_RECENT_CHAT_UPDATE,
    UPDATE_MUTE_RECENT_CHAT,
    UPDATE_ARCHIVE_RECENT_CHAT,
    UPDATE_PIN_RECENT_CHAT,
    UPDATE_FAVOURITE_RECENT_CHAT,
    UPDATE_UNREAD_FLAG_RECENT_CHAT,
    UNREAD_MESSAGE_DELETE,
    UPDATE_MESSAGE_STATUS,
    CLEAR_ALL_CHAT,
    UPDATE_ACTIVE_CHAT_DATA,
    UPDATE_MESSAGE_REACTION,
    CLEAR_CHAT_HISTORY,
} from '../Actions/Constants';
import { updateRecall, emptyMessage } from '../Helpers/Chat/Recall';
import { MEDIA_MESSAGE_TYPES } from '../Helpers/Constants';
import { getMsgStatusInOrder, transformReactionsGrouped, updateReactionList } from '../Helpers/Chat/ChatHelper';
import {
    GROUP_CHAT_PROFILE_UPDATED_NOTIFY,
    GROUP_DISAPPEAR_UPDATED,
    PIN_STATUS_FIELD,
    FAVOURITE_STATUS_FIELD,
    UNREAD_FLAG_FIELD,
    IS_PINNED_FIELD,
    IS_FAVOURITE_FIELD,
    IS_UNREAD_FIELD,
    POLL_MESSAGE_TYPE,
} from '../Helpers/Chat/Constant';

/**
 * Deduplicates an array of recent chat entries by their conversation identifier (fromUserId).
 * For group chats the SDK maps the group UUID to fromUserId, so two API entries that belong
 * to the same group will share the same (normalized) fromUserId.  When duplicates are found
 * we keep the entry whose timestamp is the highest (i.e. the most recent message).
 *
 * This guard is applied unconditionally to the incoming payload so that the initial load,
 * server-reconnect, and pagination paths are all protected.
 */
const dedupeRecentChatsByConversation = data => {
    if (!Array.isArray(data)) return data;
    const seen = {};
    for (const obj of data) {
        if (!obj.fromUserId) continue;
        const normalizedId =
            typeof obj.fromUserId === 'string' && obj.fromUserId.includes('@')
                ? obj.fromUserId.split('@')[0]
                : obj.fromUserId;
        if (!seen[normalizedId] || (seen[normalizedId].timestamp || 0) < (obj.timestamp || 0)) {
            seen[normalizedId] = { ...obj, fromUserId: normalizedId };
        }
    }
    return Object.values(seen);
};

const hasBodyContent = body => {
    if (!body || typeof body !== 'object') return false;
    // Check for text content
    if (typeof body.message === 'string' && body.message.trim() !== '') return true;
    // Check for media or other rich types
    if (body.media || body.contact || body.location || body.meet || body.sticker || body.thumbnail || body.story)
        return true;
    // Call events have no text or media; details live alongside message_type.
    if (body.message_type === 'call') return true;
    // A poll has no text; its question lives in body.poll.
    if (body.message_type === POLL_MESSAGE_TYPE || body.poll) return true;
    return false;
};

const isPollRow = row => row?.msgType === POLL_MESSAGE_TYPE || row?.msgBody?.message_type === POLL_MESSAGE_TYPE;

// A poll activity keeps the poll's msgId, so a newer server row for it must replace the stored one.
const isNewerPollRow = (incoming, existing) =>
    isPollRow(incoming) && (incoming.timestamp || 0) > (existing.timestamp || 0);

const updateRecentChat = (filterBy, newMessage, recentChatArray = []) => {
    const normalizedFilterBy = filterBy.includes('@') ? filterBy.split('@')[0] : filterBy;
    const checkalreadyExist = recentChatArray.find(message => {
        const messageId = message.fromUserId.includes('@') ? message.fromUserId.split('@')[0] : message.fromUserId;
        return messageId === normalizedFilterBy;
    });
    if (!checkalreadyExist) {
        newMessage.archiveStatus = 0;
        newMessage.fromUserId = normalizedFilterBy;
        return [...recentChatArray, newMessage];
    }
    return recentChatArray.map(recentItem => {
        const itemId = recentItem.fromUserId.includes('@')
            ? recentItem.fromUserId.split('@')[0]
            : recentItem.fromUserId;
        if (itemId === normalizedFilterBy) {
            const isSameMsg =
                newMessage.msgId && recentItem.msgId && String(newMessage.msgId) === String(recentItem.msgId);
            const statusTypes = [
                'Processing',
                'delivered',
                'carbonDelivered',
                'seen',
                'carbonSeen',
                'acknowledge',
                'carbonSentSeen',
            ];
            const isStatusType = statusTypes.includes(newMessage.msgType);
            const hasNewContent = hasBodyContent(newMessage.msgBody);
            // msgId === '' means an explicit "clear last message" dispatch (e.g. disappear expiry
            // with no remaining messages). In that case we MUST allow msgBody to be cleared even
            // though hasBodyContent({}) is false — otherwise the stale message body stays visible.
            const isExplicitClear = newMessage.msgId === '';

            return {
                ...recentItem,
                ...newMessage,
                fromUserId: normalizedFilterBy,
                // Incoming messages always report 0, so the stored flag wins.
                archiveStatus: newMessage.archiveStatus === 1 ? 1 : (recentItem.archiveStatus ?? 0),
                // CRITICAL: Robust body/type persistence.
                // Prevent receipts (empty bodies) from overwriting real message content,
                // but allow an explicit clear (msgId='') to zero out the body.
                msgBody: hasNewContent || isExplicitClear ? newMessage.msgBody : recentItem.msgBody || {},
                msgType:
                    newMessage.msgType && !isStatusType ? newMessage.msgType : recentItem.msgType || newMessage.msgType,
                // CRITICAL: Only preserve reaction metadata if it's the SAME message.
                // Reset metadata for new message IDs to fix the "Sticky Reaction" bug.
                reactions:
                    newMessage.reactions !== undefined
                        ? newMessage.reactions
                        : isSameMsg
                          ? recentItem.reactions
                          : undefined,
                reactedUserName:
                    newMessage.reactedUserName !== undefined
                        ? newMessage.reactedUserName
                        : isSameMsg
                          ? recentItem.reactedUserName
                          : undefined,
                emojiId:
                    newMessage.emojiId !== undefined ? newMessage.emojiId : isSameMsg ? recentItem.emojiId : undefined,
                messageSenderId:
                    newMessage.messageSenderId !== undefined
                        ? newMessage.messageSenderId
                        : isSameMsg
                          ? recentItem.messageSenderId
                          : undefined,
                deleteStatus:
                    newMessage.deleteStatus !== undefined
                        ? newMessage.deleteStatus
                        : isSameMsg
                          ? recentItem.deleteStatus
                          : 0,
                deletedBy:
                    newMessage.deletedBy !== undefined ? newMessage.deletedBy : isSameMsg ? recentItem.deletedBy : '',
                deleted_by:
                    newMessage.deleted_by !== undefined
                        ? newMessage.deleted_by
                        : isSameMsg
                          ? recentItem.deleted_by
                          : '',
            };
        }
        return recentItem;
    });
};

const updateMuteRecentChat = (data, stateData) => {
    if (!stateData) return [];
    return stateData.map(element => {
        if (element.fromUserId === data.fromUserId) {
            element.muteStatus = data.isMuted ? 1 : 0;
        }
        return element;
    });
};

const updateRecentUnreadCount = (data, stateData) => {
    return stateData?.map(element => {
        return element;
    });
};

const updateRecentChatMessageStatus = (data, stateData) => {
    return stateData?.map(element => {
        if (element.fromUserId === data.fromUserId && element.msgId === data.msgId) {
            element.msgStatus = getMsgStatusInOrder(element.msgStatus, data.msgStatus);
        }
        if (!element.msgType || element.msgType === undefined) {
            element.msgType = element.msgBody.message_type;
        }
        return element;
    });
};

// Rows are stored under the bare id, while archive actions may carry a full JID.
const bareId = id => (typeof id === 'string' && id.includes('@') ? id.split('@')[0] : id);

// Archived-chat sync can land before the recent-chat list is fetched, so state.data
// may still be undefined here.
const updateArchiveRecentChat = (data, stateData) => {
    if (!stateData) return [];
    const targetId = bareId(data.fromUserId);
    return stateData.map(element => {
        if (bareId(element.fromUserId) === targetId) {
            return { ...element, archiveStatus: data.isArchived ? 1 : 0 };
        }
        return element;
    });
};

// Shared by pin/favourite/mark-unread: they differ only in the flag they carry.
// Returns a new object for the touched row rather than mutating it: callers compare
// entries by reference to decide what to re-render.
const updateChatFlagRecentChat = (data, stateData, flagKey, valueKey) => {
    if (!stateData) return [];
    return stateData.map(element =>
        element.fromUserId === data.fromUserId ? { ...element, [flagKey]: data[valueKey] ? 1 : 0 } : element,
    );
};

const updateRecentChatReaction = (data, stateData) => {
    if (!stateData) return [];
    const { fromUserId, msgId, emojiId, userId, action, msgBody, msgType, lastMessage, messageSenderId, timestamp } =
        data;
    const normalizedChatId = fromUserId.includes('@') ? fromUserId.split('@')[0] : fromUserId;
    return stateData.map(element => {
        const elementId = element.fromUserId.includes('@') ? element.fromUserId.split('@')[0] : element.fromUserId;
        if (elementId === normalizedChatId) {
            const newReactions = updateReactionList(element.reactions, emojiId, userId, action);
            const hasReactions = newReactions.length > 0;

            if (action === 'add' || action === 'update') {
                // Save snapshot on first reaction ever (identified by no existing snapshot).
                // ONLY save if element.msgBody is valid - otherwise the snapshot is corrupted.
                const canSaveSnapshot = !element.prevMsgId && element.msgBody;
                const prevSnapshot = canSaveSnapshot
                    ? {
                          prevMsgId: element.msgId,
                          prevMsgBody: element.msgBody,
                          prevMsgType: element.msgType,
                          prevTimestamp: element.timestamp,
                      }
                    : {};

                // The summary names this reactor; the last group can belong to someone else.
                const finalReactedUser = userId;
                const finalEmojiId = emojiId;

                const statusTypes = [
                    'Processing',
                    'delivered',
                    'carbonDelivered',
                    'seen',
                    'carbonSeen',
                    'acknowledge',
                    'carbonSentSeen',
                ];
                const currentIsStatus = statusTypes.includes(element.msgType) || !hasBodyContent(element.msgBody);
                const incomingHasContent = hasBodyContent(msgBody);

                return {
                    ...element,
                    ...prevSnapshot,
                    msgId: msgId || element.msgId,
                    msgBody: incomingHasContent || !element.msgBody ? msgBody || element.msgBody : element.msgBody,
                    msgType: msgType && !statusTypes.includes(msgType) ? msgType : element?.msgBody?.message_type,
                    reactions: newReactions,
                    reactedUserName: finalReactedUser,
                    emojiId: finalEmojiId,
                    messageSenderId: messageSenderId ? messageSenderId : null,
                };
            }

            if (action === 'remove') {
                if (!hasReactions) {
                    const fallback = lastMessage || null;

                    const statusTypes = [
                        'Processing',
                        'delivered',
                        'carbonDelivered',
                        'seen',
                        'carbonSeen',
                        'acknowledge',
                        'carbonSentSeen',
                    ];
                    const restoredMsgId = fallback?.msgId || element.prevMsgId || element.msgId;

                    // Priority: 1) Valid fallback from action, 2) Valid snapshot, 3) Current (last resort)
                    let restoredMsgBody = fallback?.msgBody;
                    if (!hasBodyContent(restoredMsgBody)) restoredMsgBody = element.prevMsgBody;
                    if (!hasBodyContent(restoredMsgBody)) restoredMsgBody = element.msgBody;

                    let restoredMsgType =
                        fallback?.msgBody?.message_type || fallback?.msgType || element.prevMsgType || element.msgType;
                    if (statusTypes.includes(restoredMsgType)) restoredMsgType = 'text';

                    const restoredTimestamp = fallback?.timestamp || element.prevTimestamp || element.timestamp;
                    const restoredReactions = fallback?.reactions || [];
                    const lastRG = restoredReactions[restoredReactions.length - 1];
                    const restoredReactedUser = lastRG?.userIds ? lastRG.userIds[lastRG.userIds.length - 1] : '';
                    const restoredEmojiId = lastRG?.emojiId || '';

                    return {
                        ...element,
                        msgId: restoredMsgId || element.msgId,
                        msgBody: restoredMsgBody || {},
                        msgType: restoredMsgType,
                        timestamp: restoredTimestamp,
                        reactions: restoredReactions,
                        reactedUserName: restoredReactedUser,
                        emojiId: restoredEmojiId,
                        messageSenderId: messageSenderId ? messageSenderId : null,
                        prevMsgId: undefined,
                        prevMsgBody: undefined,
                        prevMsgType: undefined,
                        prevTimestamp: undefined,
                        prevReactions: undefined,
                        prevReactedUserName: undefined,
                        prevEmojiId: undefined,
                    };
                } else {
                    // Some reactions remain — update display metadata only
                    const lastReactionGroup = newReactions[newReactions.length - 1];
                    const finalReactedUser = lastReactionGroup?.userIds
                        ? lastReactionGroup.userIds[lastReactionGroup.userIds.length - 1]
                        : '';
                    const finalEmojiId = lastReactionGroup?.emojiId || '';
                    return {
                        ...element,
                        reactions: newReactions,
                        reactedUserName: finalReactedUser,
                        emojiId: finalEmojiId,
                        messageSenderId: messageSenderId ? messageSenderId : null,
                    };
                }
            }
        }
        return element;
    });
};

const updateReconnectRecentChat = (newRecentChat, oldRecentChat = []) => {
    const pendingMsgChats = oldRecentChat.filter(
        chat => chat.msgStatus === 3 && MEDIA_MESSAGE_TYPES.includes(chat.msgType),
    );

    return newRecentChat.map(recentItem => {
        const pendingChat = pendingMsgChats.find(pending => pending.fromUserId === recentItem.fromUserId);
        if (pendingChat) {
            return {
                ...pendingChat,
                ...recentItem,
            };
        } else {
            return {
                ...recentItem,
            };
        }
    });
};

const deletedChatList = (deleteData, currentArray) => {
    const { fromUserId } = deleteData;
    return currentArray.filter((element, _id) => element['fromUserId'] !== fromUserId);
};

const clearMessageInRecentChat = (data, id) => {
    return data.find(element => {
        if (element.recent.fromUserId === id) {
            element.recent.msgBody = {};
            element.recent.msgType = '';
            element.recent.createdAt = '';
            element.recent.msgStatus = 4;
            element.recent.reactions = [];
            element.recent.reactedUserName = '';
            element.recent.emojiId = '';
            element.recent.messageSenderId = '';
            return element;
        }
        return false;
    });
};

const updateMsgByLastMsgId = (newMessage, recentChatList = []) => {
    const msgIndex = recentChatList.findIndex(msg => msg.lastMsgId === newMessage.msgId);
    if (msgIndex > -1) {
        recentChatList[msgIndex] = { ...recentChatList[msgIndex], ...newMessage };
        recentChatList[msgIndex]['msgType'] = newMessage?.msgBody?.message_type || newMessage.msgType;
    }
    return recentChatList.sort((a, b) => (b.msgId > a.msgId ? 1 : -1));
};

const clearAllRecentChat = stateData => {
    if (!stateData) return [];
    return stateData.map(element => {
        if (element) {
            element.msgBody = {};
            element.msgType = '';
            element.createdAt = '';
            element.msgStatus = 4;
            element.reactions = [];
            element.reactedUserName = '';
            element.emojiId = '';
            element.messageSenderId = '';
            return element;
        }
        return element;
    });
};

export function RecentChatReducer(state = [], action = {}) {
    let { payload: { id, data, filterBy, rosterData, refreshUnreadCount } = {} } = action;
    refreshUnreadCount = !!refreshUnreadCount;
    let isServerReconnect = !!data?.serverReconnect;
    switch (action.type) {
        case RECENT_CHAT_DATA:
            // Deduplicate the incoming payload first so that API-level duplicates
            // (e.g. the same group appearing more than once) are removed before we
            // merge or store the data.  This covers the initial load, server-reconnect
            // and pagination paths alike.
            if (action.payload.data && Array.isArray(action.payload.data)) {
                action.payload.data = dedupeRecentChatsByConversation(action.payload.data);
            }
            if (state.data && !isServerReconnect) {
                let newpayLoadData = action.payload.data.filter(newObj => {
                    return !state.data.some(
                        existingObj => existingObj.msgId === newObj.msgId && !isNewerPollRow(newObj, existingObj),
                    );
                });
                let constructData = [...state.data, ...newpayLoadData];
                const latestObjects = constructData.reduce((acc, obj) => {
                    const normalizedId = obj.fromUserId.includes('@') ? obj.fromUserId.split('@')[0] : obj.fromUserId;
                    if (!acc[normalizedId] || (acc[normalizedId].timestamp || 0) <= (obj.timestamp || 0)) {
                        const existing = acc[normalizedId];
                        // When a synthetic disappear banner (msgId ends with '_disappear') would be
                        // overwritten by a server groupProfileUpdated entry at the same timestamp,
                        // preserve the publisher/disappear fields from the synthetic banner.
                        // The server returns the local user as publisherId for received profile updates,
                        // which would cause the recent chat to incorrectly show "You changed..." instead
                        // of the actual publisher's name.
                        if (
                            existing &&
                            existing.msgType === GROUP_CHAT_PROFILE_UPDATED_NOTIFY &&
                            typeof existing.msgId === 'string' &&
                            existing.msgId.endsWith('_disappear') &&
                            obj.msgType === GROUP_CHAT_PROFILE_UPDATED_NOTIFY &&
                            existing.profileUpdatedStatus === GROUP_DISAPPEAR_UPDATED &&
                            existing.publisherId
                        ) {
                            acc[normalizedId] = {
                                ...obj,
                                fromUserId: normalizedId,
                                publisherId: existing.publisherId,
                                profileUpdatedStatus: existing.profileUpdatedStatus,
                                ...(existing.msgDisappear !== undefined && { msgDisappear: existing.msgDisappear }),
                            };
                        } else {
                            acc[normalizedId] = {
                                ...obj,
                                fromUserId: normalizedId,
                                // Server rows report 0 for locally archived chats.
                                archiveStatus:
                                    obj.archiveStatus === 1 ? 1 : (existing?.archiveStatus ?? obj.archiveStatus ?? 0),
                            };
                        }
                    }
                    return acc;
                }, {});
                action.payload.data = Object.values(latestObjects);
            }
            if (action.payload.data && Array.isArray(action.payload.data)) {
                action.payload.data = action.payload.data.map(recent => ({
                    ...recent,
                    fromUserId: recent.fromUserId.includes('@') ? recent.fromUserId.split('@')[0] : recent.fromUserId,
                    reactions: transformReactionsGrouped(recent.reactions),
                }));
            }
            return action.payload;
        case RECONNECT_RECENT_CHAT_UPDATE:
            return {
                ...state,
                id,
                data: updateReconnectRecentChat(action.payload?.data, state.data),
                refreshUnreadCount,
            };
        case UPDATE_MESSAGE_STATUS:
            return {
                ...state,
                id,
                data: updateRecentChatMessageStatus(action.payload?.data, state.data),
                refreshUnreadCount,
            };
        case UPDATE_MESSAGE_REACTION:
            return {
                ...state,
                id,
                data: updateRecentChatReaction(action.payload?.data, state.data),
                refreshUnreadCount,
            };
        case ACTIVE_CHAT_UPDATE:
            return {
                ...state,
                id,
                data: updateRecentChat(filterBy, data, state.data),
                refreshUnreadCount,
            };
        case RECENT_NOTIFICATION_UPDATE:
            return {
                ...state,
                data: updateRecentChat(filterBy, data, state.data),
                refreshUnreadCount,
            };
        case RECENT_REMOVE_MESSAGE_UPDATE:
            return {
                ...state,
                id,
                data: emptyMessage(data, state.data),
                refreshUnreadCount,
            };
        case RECENT_RECALL_UPDATE:
            return {
                ...state,
                id,
                data: updateRecall(data, state.data),
                refreshUnreadCount,
            };

        case RECENT_ROSTER_UPDATE:
            return {
                ...state,
                rosterData,
                refreshUnreadCount,
            };
        case DELETE_SINGLE_CHAT:
            return {
                ...state,
                id,
                data: deletedChatList(data, state.data),
                refreshUnreadCount,
            };
        case UPDATE_ROSTER_LAST_MESSAGE:
            const recentChatNames = state?.rosterData?.recentChatNames || [];
            const recentChatItems = state?.rosterData?.recentChatItems || [];

            return {
                ...state,
                id,
                ...state.data,
                rosterData: [...recentChatNames, clearMessageInRecentChat(recentChatItems, data)],
                refreshUnreadCount,
            };
        case RESET_RECENT_CHAT:
            return [];
        case UPDATE_MSG_BY_LAST_MSGID:
            return {
                ...state,
                id,
                data: updateMsgByLastMsgId(data, state.data),
                refreshUnreadCount,
            };
        case UPDATE_MUTE_RECENT_CHAT:
            return {
                ...state,
                id,
                data: updateMuteRecentChat(data, state.data),
                refreshUnreadCount,
            };
        case UPDATE_ARCHIVE_RECENT_CHAT:
            return {
                ...state,
                id,
                data: updateArchiveRecentChat(data, state.data),
                refreshUnreadCount,
            };
        case UPDATE_PIN_RECENT_CHAT:
            return {
                ...state,
                id,
                data: updateChatFlagRecentChat(data, state.data, PIN_STATUS_FIELD, IS_PINNED_FIELD),
                refreshUnreadCount,
            };
        case UPDATE_FAVOURITE_RECENT_CHAT:
            return {
                ...state,
                id,
                data: updateChatFlagRecentChat(data, state.data, FAVOURITE_STATUS_FIELD, IS_FAVOURITE_FIELD),
                refreshUnreadCount,
            };
        case UPDATE_UNREAD_FLAG_RECENT_CHAT:
            return {
                ...state,
                id,
                data: updateChatFlagRecentChat(data, state.data, UNREAD_FLAG_FIELD, IS_UNREAD_FIELD),
                refreshUnreadCount,
            };
        case UNREAD_MESSAGE_DELETE:
            return {
                ...state,
                id,
                data: updateRecentUnreadCount(data, state.data),
                refreshUnreadCount,
            };
        case CLEAR_ALL_CHAT:
            return {
                ...state,
                id,
                data: clearAllRecentChat(state.data),
            };

        case CLEAR_CHAT_HISTORY:
            if (!state.data) return state;
            return {
                ...state,
                id,
                data: state.data.map(element => {
                    const elementId = element.fromUserId.includes('@')
                        ? element.fromUserId.split('@')[0]
                        : element.fromUserId;
                    const targetId = action.payload.data.fromUserId.includes('@')
                        ? action.payload.data.fromUserId.split('@')[0]
                        : action.payload.data.fromUserId;
                    if (elementId === targetId) {
                        return {
                            ...element,
                            msgBody: {},
                            msgType: '',
                            createdAt: '',
                            msgStatus: 4,
                            reactions: [],
                            reactedUserName: '',
                            emojiId: '',
                            messageSenderId: '',
                        };
                    }
                    return element;
                }),
            };

        default:
            return state;
    }
}

export function ActiveChatReducer(state = [], action = {}) {
    let { payload } = action;
    if (action.type === ACTIVE_CHAT_DATA && payload?.data?.recent) {
        payload.data.recent.reactions = transformReactionsGrouped(payload.data.recent.reactions);
    }
    switch (action.type) {
        case ACTIVE_CHAT_DATA:
            return action.payload;
        case UPDATE_GROUP_NAME: {
            if (!state.data) return state;
            const { nickName } = action.payload.data.groups[0];
            return {
                ...state,
                id: action.payload.id,
                data: {
                    ...state.data,
                    roster: {
                        ...state.data.roster,
                        groupName: nickName,
                        nickName,
                    },
                },
            };
        }
        case UPDATE_MUTE_RECENT_CHAT:
            if (!state.data) return state;
            {
                const {
                    chatId,
                    recent: { muteStatus },
                } = state.data || {};
                const { fromUserId, isMuted } = action.payload.data;
                const newMuteStatus = chatId === fromUserId ? Number(isMuted) : muteStatus;
                return {
                    ...state,
                    id: uuidv4(),
                    data: {
                        ...state.data,
                        recent: {
                            ...state.data.recent,
                            muteStatus: newMuteStatus,
                        },
                    },
                };
            }
        case UPDATE_ARCHIVE_RECENT_CHAT:
            if (!state.data) return state;
            {
                const {
                    chatId,
                    recent: { archiveStatus },
                } = state.data || {};
                const { fromUserId, isArchived } = action.payload.data;
                const newArchiveStatus = bareId(chatId) === bareId(fromUserId) ? Number(isArchived) : archiveStatus;
                return {
                    ...state,
                    id: uuidv4(),
                    data: {
                        ...state.data,
                        recent: {
                            ...state.data.recent,
                            archiveStatus: newArchiveStatus,
                        },
                    },
                };
            }
        case UPDATE_MESSAGE_REACTION:
            if (!state.data) return state;
            {
                const {
                    fromUserId,
                    msgId,
                    emojiId,
                    userId,
                    action: reactionAction,
                    msgBody,
                    msgType,
                    lastMessage,
                    timestamp,
                    messageSenderId,
                } = action.payload.data;
                const { chatId, recent } = state.data;
                if (chatId === fromUserId) {
                    const hadNoReactions = !recent.reactions || recent.reactions.length === 0;
                    const newReactions = updateReactionList(recent.reactions, emojiId, userId, reactionAction);
                    const hasReactions = newReactions.length > 0;

                    if (reactionAction === 'add' || reactionAction === 'update') {
                        const prevSnapshot = hadNoReactions
                            ? {
                                  prevMsgId: recent.msgId,
                                  prevMsgBody: recent.msgBody,
                                  prevMsgType: recent.msgType,
                                  prevTimestamp: recent.timestamp,
                              }
                            : {};

                        const fallback = lastMessage || null;
                        const restoredMsgId = fallback?.msgId || msgId || recent.msgId;
                        const restoredMsgBody = fallback?.msgBody || msgBody || recent.msgBody;
                        const restoredMsgType =
                            fallback?.msgType || fallback?.msgBody?.message_type || msgType || recent.msgType;
                        const restoredTimestamp =
                            fallback?.activityTime || fallback?.timestamp || timestamp || recent.timestamp;
                        const restoredReactions = fallback?.reactions || newReactions;

                        const lastReactionGroup = restoredReactions[restoredReactions.length - 1];
                        const restoredReactedUser = lastReactionGroup?.userIds
                            ? lastReactionGroup.userIds[lastReactionGroup.userIds.length - 1]
                            : fallback?.reactedUserName || userId;
                        const restoredEmojiId = lastReactionGroup?.emojiId || fallback?.emojiId || emojiId;

                        return {
                            ...state,
                            id: uuidv4(),
                            data: {
                                ...state.data,
                                recent: {
                                    ...recent,
                                    ...prevSnapshot,
                                    msgId: restoredMsgId,
                                    msgBody: restoredMsgBody,
                                    msgType: restoredMsgType,
                                    // timestamp: restoredTimestamp,
                                    reactions: restoredReactions,
                                    reactedUserName: restoredReactedUser,
                                    emojiId: restoredEmojiId,
                                    messageSenderId: messageSenderId || recent.messageSenderId,
                                },
                            },
                        };
                    }

                    if (reactionAction === 'remove') {
                        if (!hasReactions) {
                            const fallback = lastMessage || null;
                            const restoredMsgId = fallback?.msgId || recent.prevMsgId || recent.msgId;
                            const restoredMsgBody = fallback?.msgBody || recent.prevMsgBody || recent.msgBody;
                            const restoredMsgType =
                                fallback?.msgBody?.message_type ||
                                fallback?.msgType ||
                                recent.prevMsgType ||
                                recent.msgBody?.msgType;
                            const restoredTimestamp =
                                fallback?.activityTime ||
                                fallback?.timestamp ||
                                timestamp ||
                                recent.prevTimestamp ||
                                recent.timestamp;
                            const restoredReactions = fallback?.reactions || [];

                            const lastReactionGroup = restoredReactions[restoredReactions.length - 1];
                            const restoredReactedUser = lastReactionGroup?.userIds
                                ? lastReactionGroup.userIds[lastReactionGroup.userIds.length - 1]
                                : fallback?.reactedUserName || '';
                            const restoredEmojiId = lastReactionGroup?.emojiId || fallback?.emojiId || '';
                            const statusTypes = [
                                'Processing',
                                'delivered',
                                'carbonDelivered',
                                'seen',
                                'carbonSeen',
                                'acknowledge',
                                'carbonSentSeen',
                            ];

                            return {
                                ...state,
                                id: uuidv4(),
                                data: {
                                    ...state.data,
                                    recent: {
                                        ...recent,
                                        msgId: restoredMsgId,
                                        msgBody: hasBodyContent(restoredMsgBody)
                                            ? restoredMsgBody
                                            : recent.msgBody || {},
                                        msgType:
                                            restoredMsgType && !statusTypes.includes(restoredMsgType)
                                                ? restoredMsgType
                                                : recent.msgBody?.msgType || 'text',
                                        // timestamp: restoredTimestamp,
                                        reactions: restoredReactions,
                                        reactedUserName: restoredReactedUser,
                                        emojiId: restoredEmojiId,
                                        messageSenderId: messageSenderId || recent.messageSenderId,
                                        prevMsgId: undefined,
                                        prevMsgBody: undefined,
                                        prevMsgType: undefined,
                                        prevTimestamp: undefined,
                                    },
                                },
                            };
                        } else {
                            const fallback = lastMessage || null;
                            const restoredMsgId = fallback?.msgId || recent.msgId;
                            const restoredMsgBody = fallback?.msgBody || recent.msgBody;
                            const restoredMsgType =
                                fallback?.msgType || fallback?.msgBody?.message_type || recent.msgType;
                            const restoredTimestamp =
                                fallback?.activityTime || fallback?.timestamp || timestamp || recent.timestamp;
                            const restoredReactions = fallback?.reactions || newReactions;

                            const lastReactionGroup = restoredReactions[restoredReactions.length - 1];
                            const restoredReactedUser = lastReactionGroup?.userIds
                                ? lastReactionGroup.userIds[lastReactionGroup.userIds.length - 1]
                                : fallback?.reactedUserName || '';
                            const restoredEmojiId = lastReactionGroup?.emojiId || fallback?.emojiId || '';

                            return {
                                ...state,
                                id: uuidv4(),
                                data: {
                                    ...state.data,
                                    recent: {
                                        ...recent,
                                        msgId: restoredMsgId,
                                        msgBody: restoredMsgBody,
                                        msgType: restoredMsgType,
                                        // timestamp: restoredTimestamp,
                                        reactions: restoredReactions,
                                        reactedUserName: restoredReactedUser,
                                        emojiId: restoredEmojiId,
                                        messageSenderId: messageSenderId || recent.messageSenderId,
                                    },
                                },
                            };
                        }
                    }
                }
                return state;
            }
        case UPDATE_ACTIVE_CHAT_DATA:
            if (!state.data) return state;
            {
                const isSameMsg =
                    action.payload.data.msgId &&
                    state.data.recent.msgId &&
                    String(action.payload.data.msgId) === String(state.data.recent.msgId);
                const statusTypes = [
                    'Processing',
                    'delivered',
                    'carbonDelivered',
                    'seen',
                    'carbonSeen',
                    'acknowledge',
                    'carbonSentSeen',
                ];
                const isStatusType = statusTypes.includes(action.payload.data.msgType);
                const hasNewContent = hasBodyContent(action.payload.data.msgBody);

                const reactionClearing = isSameMsg
                    ? {}
                    : {
                          reactions: undefined,
                          reactedUserName: undefined,
                          emojiId: undefined,
                          messageSenderId: undefined,
                      };
                return {
                    ...state,
                    id: uuidv4(),
                    data: {
                        ...state.data,
                        recent: {
                            ...state.data.recent,
                            ...reactionClearing,
                            ...action.payload.data,
                            msgBody: hasNewContent ? action.payload.data.msgBody : state.data.recent.msgBody || {},
                            msgType:
                                action.payload.data.msgType && !isStatusType
                                    ? action.payload.data.msgType
                                    : state.data.recent.msgType || action.payload.data.msgType,
                        },
                    },
                };
            }

        case RESET_TO_DEFAULT:
            return [];
        default:
            return state;
    }
}

export function WebChatProfileImageReducer(state = [], action = {}) {
    if (action.type === PROFILE_IMAGE_CHAT_DATA) {
        return action.payload;
    }
    return state;
}

const initialState = {
    modalPopup: false,
};
export function modalPopUpReducer(state = initialState, action = {}) {
    if (action.type === MODAL_ACTIVE_CLASS) {
        return {
            ...state,
            modalPopup: action.payload,
        };
    }
    return state;
}
